import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { AmazonFinanceValidationResult, FinanceConfiguration } from '../../services/amazon-finance.models';
import { AmazonFinanceService } from '../../services/amazon-finance.service';

type MapField =
  | 'marketplaceToCustomer'
  | 'customerToAccount'
  | 'deliveryCountryToAccount'
  | 'deliveryCountryToShipmentAccount'
  | 'deliveryCountryToCostCenter'
  | 'productToAccount'
  | 'productMappings';

@Component({
  selector: 'gung-amazon-finance-configuration',
  templateUrl: './amazon-finance-configuration.component.html',
  styleUrl: './amazon-finance-configuration.component.scss',
  standalone: false
})
export class AmazonFinanceConfigurationComponent implements OnInit {
  amazon: FinanceConfiguration | null = null;
  validation: AmazonFinanceValidationResult | null = null;
  mapText = {} as Record<MapField, string>;
  customerRevenueHeaders = '{}';
  amazonFeeHeaders = '{}';
  loading = false;
  saving = false;
  saved = false;
  error = '';

  readonly mapFields: { key: MapField; label: string; numeric: boolean; hint: string }[] = [
    { key: 'marketplaceToCustomer', label: 'MARKETPLACE_TO_CUSTOMER', numeric: false, hint: 'Amazon.se = AMAZON-SE' },
    { key: 'customerToAccount', label: 'CUSTOMER_TO_ACCOUNT', numeric: true, hint: 'AMAZON-SE = 3010' },
    { key: 'deliveryCountryToAccount', label: 'COUNTRY_TO_REVENUE_ACCOUNT', numeric: true, hint: 'SE = 3010' },
    {
      key: 'deliveryCountryToShipmentAccount',
      label: 'COUNTRY_TO_SHIPPING_ACCOUNT',
      numeric: true,
      hint: 'SE = 3520'
    },
    { key: 'deliveryCountryToCostCenter', label: 'COUNTRY_TO_COST_CENTER', numeric: false, hint: 'SE = ONLINE' },
    { key: 'productToAccount', label: 'PRODUCT_TO_ACCOUNT', numeric: true, hint: 'DEFAULT = 3010' },
    { key: 'productMappings', label: 'AMAZON_TO_ERP_PRODUCT', numeric: false, hint: 'Amazon product = ERP product' }
  ];

  constructor(private readonly financeService: AmazonFinanceService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.financeService
      .getConfiguration()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: result => {
          this.amazon = result.configuration;
          this.validation = result.validation;
          this.populateTextFields();
        },
        error: () => (this.error = 'AMAZON_FINANCE_CONFIGURATION_LOAD_ERROR')
      });
  }

  validate(): void {
    const prepared = this.prepareConfiguration();
    if (!prepared) return;
    this.financeService.validateConfiguration(prepared).subscribe({
      next: validation => (this.validation = validation),
      error: () => (this.error = 'AMAZON_FINANCE_VALIDATION_ERROR')
    });
  }

  save(): void {
    const prepared = this.prepareConfiguration();
    if (!prepared) return;
    this.saving = true;
    this.saved = false;
    this.error = '';
    this.financeService.validateConfiguration(prepared).subscribe({
      next: validation => {
        this.validation = validation;
        if (validation.errors.length) {
          this.saving = false;
          return;
        }
        this.financeService
          .saveConfiguration(prepared)
          .pipe(finalize(() => (this.saving = false)))
          .subscribe({
            next: result => {
              this.amazon = result.configuration;
              this.validation = result.validation;
              this.saved = true;
              this.populateTextFields();
            },
            error: error => (this.error = error?.error?.message || 'AMAZON_FINANCE_SAVE_ERROR')
          });
      },
      error: () => {
        this.saving = false;
        this.error = 'AMAZON_FINANCE_VALIDATION_ERROR';
      }
    });
  }

  private prepareConfiguration(): FinanceConfiguration | null {
    if (!this.amazon) return null;
    this.error = '';
    try {
      return {
        ...this.amazon,
        marketplaceToCustomer: this.parseMap(this.mapText.marketplaceToCustomer, false),
        customerToAccount: this.parseMap(this.mapText.customerToAccount, true),
        deliveryCountryToAccount: this.parseMap(this.mapText.deliveryCountryToAccount, true),
        deliveryCountryToShipmentAccount: this.parseMap(this.mapText.deliveryCountryToShipmentAccount, true),
        deliveryCountryToCostCenter: this.parseMap(this.mapText.deliveryCountryToCostCenter, false),
        productToAccount: this.parseMap(this.mapText.productToAccount, true),
        productMappings: this.parseMap(this.mapText.productMappings, false),
        customerRevenueHeaderMappings: this.parseJson(this.customerRevenueHeaders, 'CUSTOMER_REVENUE_HEADERS'),
        amazonFeeCostHeaderMappings: this.parseJson(this.amazonFeeHeaders, 'AMAZON_FEE_HEADERS')
      };
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'AMAZON_FINANCE_CONFIGURATION_PARSE_ERROR';
      return null;
    }
  }

  private populateTextFields(): void {
    if (!this.amazon) return;
    this.mapFields.forEach(field => {
      this.mapText[field.key] = this.mapToText(
        (this.amazon as unknown as Record<string, Record<string, string | number>>)[field.key]
      );
    });
    this.customerRevenueHeaders = JSON.stringify(this.amazon.customerRevenueHeaderMappings || {}, null, 2);
    this.amazonFeeHeaders = JSON.stringify(this.amazon.amazonFeeCostHeaderMappings || {}, null, 2);
  }

  private mapToText(map: Record<string, string | number>): string {
    return Object.entries(map || {})
      .map(([key, value]) => `${key} = ${value}`)
      .join('\n');
  }

  private parseMap(value: string, numeric: boolean): Record<string, any> {
    const result: Record<string, any> = {};
    (value || '')
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .forEach((line, index) => {
        const separator = line.indexOf('=');
        if (separator < 1) throw new Error(`${'INVALID_MAPPING_LINE'} ${index + 1}: ${line}`);
        const key = line.slice(0, separator).trim();
        const rawValue = line.slice(separator + 1).trim();
        if (!key || !rawValue) throw new Error(`${'INVALID_MAPPING_LINE'} ${index + 1}: ${line}`);
        if (numeric) {
          const number = Number(rawValue);
          if (!Number.isFinite(number)) throw new Error(`${'INVALID_NUMBER_ON_LINE'} ${index + 1}: ${rawValue}`);
          result[key] = number;
        } else {
          result[key] = rawValue;
        }
      });
    return result;
  }

  private parseJson(value: string, label: string): Record<string, unknown> {
    try {
      const parsed = JSON.parse(value || '{}');
      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') throw new Error();
      return parsed;
    } catch {
      throw new Error(`${label}: INVALID_JSON`);
    }
  }
}
